import { useCallback, useEffect, useRef } from 'react';
import HelloSign from 'hellosign-embedded';
import { useSnackbar } from 'notistack';

import { SubmitActionTask } from 'modules/Tasks/types';
import { TaskStatus, WaitingOnReason } from 'modules/common/components/MemberOperator/MemberOperator.interfaces';
import { useMemberOperatorTaskContext } from 'modules/common/components/MemberOperator';
import { isProduction } from 'modules/common/config';
import { useMember } from 'modules/common/queries/member';
import helloSignClient from 'modules/common/helloSignClient';
import useCreateSignSS4Request from 'services/onboarding/mutations/useCreateSignSS4Request';
import useInvalidateMemberTaskQueries from 'services/memberTasks/useInvalidateMemberTaskQueries';
import { sendDatadogRumError } from 'utils/analytics/datadogRum';

interface SignSS4ModalProps {
    open: boolean;
    onClose: () => void;
    task: SubmitActionTask;
}

// Custom task card for the sign_ss4 member task. Launches the existing HelloSign
// SS-4 signing flow (no dialog of its own — HelloSign renders its own overlay).
//
// Lifecycle:
//   - Member finishes signing (SIGN): move the task to WAITING. Members never see
//     WAITING tasks (backend MEMBER_ACTIVE_TASK_STATUSES = ready/in_progress), so the
//     card leaves every feed on the next refetch and stays gone regardless of webhook
//     timing. The HelloSign webhook then moves WAITING -> COMPLETED once the signed
//     document is stored.
//   - Member exits without signing (CANCEL / CLOSE / ERROR): leave the task as-is
//     (READY), so its card stays actionable. Members don't cancel; operators remove
//     the task from the Hub if needed.
export default function SignSS4Modal({ open, onClose, task }: Readonly<SignSS4ModalProps>) {
    const { enqueueSnackbar } = useSnackbar();
    const { data: member } = useMember();
    const { mutate: createSignSS4Request } = useCreateSignSS4Request();
    const { updateTaskStatus } = useMemberOperatorTaskContext();
    const invalidateMemberTaskQueries = useInvalidateMemberTaskQueries();

    const openedRef = useRef(false);
    // Latches the signed task's id so a duplicate SIGN event is a no-op and a lingering CTA
    // can't re-fire, while a reused modal can still sign a different task.
    const signedTaskIdRef = useRef<SubmitActionTask['id'] | null>(null);
    // So async createSignSS4Request callbacks don't act on a surface the member has
    // already dismissed.
    const isOpenRef = useRef(open);
    useEffect(() => {
        isOpenRef.current = open;
    }, [open]);

    const onSigned = useCallback(() => {
        if (signedTaskIdRef.current === task.id) {
            return;
        }
        signedTaskIdRef.current = task.id;
        enqueueSnackbar('Your SS-4 has been signed — finalizing your document.', { variant: 'success' });
        // Invalidate in onSuccess so the refetch reflects WAITING instead of racing the write.
        updateTaskStatus(
            { status: TaskStatus.WAITING, waiting_on: WaitingOnReason.EXTERNAL },
            { onSuccess: () => invalidateMemberTaskQueries() }
        );
        onClose();
    }, [enqueueSnackbar, updateTaskStatus, invalidateMemberTaskQueries, task.id, onClose]);

    // Member exited without signing — leave the task untouched (stays READY / actionable) and
    // just dismiss the surface. Members don't cancel; operators remove the task via the Hub.
    const onDismissed = useCallback(() => {
        onClose();
    }, [onClose]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        helloSignClient.on(HelloSign.events.SIGN, onSigned);
        helloSignClient.on(HelloSign.events.CANCEL, onDismissed);
        helloSignClient.on(HelloSign.events.CLOSE, onDismissed);
        helloSignClient.on(HelloSign.events.ERROR, onDismissed);
        return () => {
            helloSignClient.off(HelloSign.events.SIGN);
            helloSignClient.off(HelloSign.events.CANCEL);
            helloSignClient.off(HelloSign.events.CLOSE);
            helloSignClient.off(HelloSign.events.ERROR);
        };
    }, [open, onSigned, onDismissed]);

    useEffect(() => {
        if (!open) {
            openedRef.current = false;
            return;
        }
        if (openedRef.current || signedTaskIdRef.current === task.id || !member) {
            return;
        }
        openedRef.current = true;

        createSignSS4Request(
            { email: member.email },
            {
                onSuccess: (response) => {
                    if (!isOpenRef.current) {
                        return;
                    }
                    helloSignClient.open(response.sign_url, {
                        skipDomainVerification: !isProduction(),
                        allowCancel: true,
                    });
                },
                onError: (error) => {
                    if (!isOpenRef.current) {
                        return;
                    }
                    sendDatadogRumError('SignSS4Modal.createSignSS4Request', error, { context: 'sign_ss4_task' });
                    enqueueSnackbar('Failed to open the SS-4 for signing. Please try again.', { variant: 'error' });
                    onClose();
                },
            }
        );
    }, [open, member, task.id, createSignSS4Request, enqueueSnackbar, onClose]);

    return null;
}
